/**
 * PlaudRecordingCard — Compact card displaying a Plaud recording.
 *
 * Shows: name, date, duration, account link status, linked contacts.
 * Click to expand and show summary, or open transcript viewer.
 */

import React, { useState } from 'react';
import { Mic, Clock, Calendar, ChevronDown, ChevronRight, FileText, Link2, User } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function PlaudRecordingCard({ recording, onViewTranscript, onLinkAccount, contacts = [], compact = false }) {
  const [expanded, setExpanded] = useState(false);

  const {
    id,
    name,
    recorded_at,
    duration_formatted,
    summary,
    account_name,
    account_id,
    embedding_status,
    chunks_count,
    linked_contact_ids,
  } = recording;

  const hasEmbeddings = embedding_status === 'complete' && chunks_count > 0;
  const isLinked = !!account_id;

  // Get linked contact names
  const linkedContacts = (linked_contact_ids || [])
    .map(cid => contacts.find(c => c.id === cid))
    .filter(Boolean)
    .slice(0, 3); // Show max 3 contacts

  if (compact) {
    return (
      <div className="aw-plaud-card aw-plaud-compact">
        <div className="aw-plaud-head">
          <span className="aw-plaud-icon"><Mic size={14} /></span>
          <span className="aw-plaud-name" title={name}>{name}</span>
        </div>
        <div className="aw-plaud-meta">
          {recorded_at && <span className="aw-time">{fmtDate(recorded_at)}</span>}
          {duration_formatted && <span className="aw-duration">{duration_formatted}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className={`aw-plaud-card ${expanded ? 'aw-plaud-expanded' : ''}`}>
      <div
        className="aw-plaud-header"
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === 'Enter' && setExpanded(!expanded)}
      >
        <div className="aw-plaud-toggle">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>
        <div className="aw-plaud-icon"><Mic size={16} /></div>
        <div className="aw-plaud-info">
          <span className="aw-plaud-name">{name}</span>
          <div className="aw-plaud-meta">
            {recorded_at && (
              <span className="aw-plaud-meta-item">
                <Calendar size={11} /> {fmtDate(recorded_at)}
              </span>
            )}
            {duration_formatted && (
              <span className="aw-plaud-meta-item">
                <Clock size={11} /> {duration_formatted}
              </span>
            )}
            {isLinked && account_name && (
              <span className="aw-chip aw-chip-linked">{account_name}</span>
            )}
            {hasEmbeddings && (
              <span className="aw-chip aw-chip-searchable" title={`${chunks_count} searchable chunks`}>
                Searchable
              </span>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="aw-plaud-body">
          {summary ? (
            <p className="aw-plaud-summary">{summary}</p>
          ) : (
            <p className="aw-plaud-no-summary">No AI summary available</p>
          )}

          {/* Contact chips */}
          {linkedContacts.length > 0 && (
            <div className="aw-plaud-contact-chips">
              {linkedContacts.map((contact) => (
                <span key={contact.id} className="aw-plaud-contact-chip">
                  <User size={10} />
                  {contact.first_name} {contact.last_name?.[0] || ''}
                </span>
              ))}
              {(linked_contact_ids?.length || 0) > 3 && (
                <span className="aw-plaud-contact-chip">
                  +{linked_contact_ids.length - 3} more
                </span>
              )}
            </div>
          )}

          <div className="aw-plaud-actions">
            {onViewTranscript && (
              <button
                className="aw-plaud-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewTranscript(recording);
                }}
              >
                <FileText size={14} /> View Transcript
              </button>
            )}
            {!isLinked && onLinkAccount && (
              <button
                className="aw-plaud-btn aw-plaud-btn-link"
                onClick={(e) => {
                  e.stopPropagation();
                  onLinkAccount(recording);
                }}
              >
                <Link2 size={14} /> Link to Account
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PlaudRecordingCard;
